import { useMemo } from "react";
import { EmpreendimentoBlocoCompletoTable } from "../../../../../incorporacao/domain/models/empreendimentoBlocoTable";
import { IncorporacaoImovel } from "../../../../../incorporacao/domain/models/incorporacao_imovel";

/**
 * ObraEmpreendimentoBlocos
 *
 * Exibe os blocos, andares e unidades do empreendimento vinculado à obra,
 * no mesmo visual da tabela de blocos da página Empreendimento_cadastro.
 * Ao clicar em uma unidade, navega para a área de checklist vinculada a ela.
 */

type Props = {
  blocos: EmpreendimentoBlocoCompletoTable[];
  loading: boolean;
  onClickImovel: (idObraAreaChecklist: number, openNewTab: boolean) => void;
};

export const ObraEmpreendimentoBlocos = ({ blocos, loading, onClickImovel }: Props) => {
  if (loading && blocos.length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 p-4">Carregando...</div>;
  }

  if (blocos.length === 0) {
    return <div className="text-sm text-gray-500 dark:text-gray-400 p-4">Nenhum bloco cadastrado para este empreendimento.</div>;
  }

  return (
    <div className="flex flex-wrap gap-4 py-2">
      {blocos.map((bloco, i) => (
        <BlocoCard
          key={bloco.listIncorporacaoImovel[0]?.idIncorporacaoBloco ?? `bloco-${i}`}
          bloco={bloco}
          onClickImovel={onClickImovel}
        />
      ))}
    </div>
  );
}

/**
 * BlocoCard
 */

const UNIDADES_POR_LINHA_CASA = 10;

type Linha = {
  key: string;
  label: string | null;
  imoveis: IncorporacaoImovel[];
};

type BlocoCardProps = {
  bloco: EmpreendimentoBlocoCompletoTable;
  onClickImovel: (idObraAreaChecklist: number, openNewTab: boolean) => void;
};

const BlocoCard = ({ bloco, onClickImovel }: BlocoCardProps) => {
  const isApartamento = bloco.listIncorporacaoAndar.length > 0;

  // Apartamentos: um andar por linha, do mais alto para o mais baixo.
  // Casas: linhas de UNIDADES_POR_LINHA_CASA unidades, sem rótulo de andar.
  const linhas = useMemo((): Linha[] => {
    if (isApartamento) {
      return [...bloco.listIncorporacaoAndar].reverse().map((andar) => ({
        key: `andar-${andar.idIncorporacaoAndar}`,
        label: `${andar.nroAndar}°`,
        imoveis: bloco.listIncorporacaoImovel.filter(y => y.idIncorporacaoAndar === andar.idIncorporacaoAndar),
      }));
    }

    const result: Linha[] = [];
    for (let i = 0; i < bloco.listIncorporacaoImovel.length; i += UNIDADES_POR_LINHA_CASA) {
      result.push({
        key: `linha-${i}`,
        label: null,
        imoveis: bloco.listIncorporacaoImovel.slice(i, i + UNIDADES_POR_LINHA_CASA),
      });
    }
    return result;
  }, [bloco, isApartamento]);

  const totalUnidades = bloco.listIncorporacaoImovel.length;

  return (
    <div className="w-fit max-w-full rounded-lg border border-gray-200 bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">

      <div className="flex items-center justify-between gap-6 px-4 pt-3 pb-2">
        <span className="text-base font-semibold text-gray-800 dark:text-white">
          {bloco.nomeBloco ?? 'Bloco'}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {totalUnidades} {totalUnidades === 1 ? 'unidade' : 'unidades'}
        </span>
      </div>

      <div className="mx-4 h-1 rounded-full bg-primary-green" />

      <div className="overflow-x-auto px-4 py-4">
        <div className="flex flex-col gap-2">
          {linhas.map((linha) => (
            <div key={linha.key} className="flex items-center gap-2">
              {isApartamento &&
                <div className="w-8 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                  {linha.label}
                </div>
              }
              {linha.imoveis.map((y) => {
                const idObraAreaChecklist = y.idObraAreaChecklist;

                return (
                  <button
                    type="button"
                    key={y.idIncorporacaoImovel}
                    disabled={!idObraAreaChecklist}
                    title={idObraAreaChecklist ? undefined : 'Unidade sem área de checklist vinculada'}
                    onMouseDown={(e) => {
                      if (!idObraAreaChecklist) return;
                      // Botão do meio ou Ctrl+clique abre em nova aba, igual à coluna da tabela.
                      onClickImovel(idObraAreaChecklist, e.button === 1 || e.ctrlKey);
                    }}
                    className="flex min-h-[2.75rem] min-w-[4.5rem] items-center justify-center rounded-md bg-gray-200 px-2 text-center text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-gray-200 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500 dark:disabled:hover:bg-gray-600">
                    {y.descricao}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="px-4 pb-3 text-xs text-gray-500 dark:text-gray-400">
        {totalUnidades} {isApartamento ? 'apartamentos' : 'casas'}
        {isApartamento && ` · ${bloco.listIncorporacaoAndar.length} andares`}
      </div>
    </div>
  );
}
